// Route paths that IAS deployments deliberately do NOT expose. Applying the
// filter (see `./filterIasBlockedRoutes`) drops these subtrees from the admin
// route table so react-router never mounts them — direct URL navigation falls
// through to the 404 handler, and any upstream nav element pointing at them
// silently renders nothing.
//
// Kept as full ancestor-relative paths so a `Route` element's props alone
// aren't enough to decide — we need the accumulated path from the root. This
// avoids accidentally hiding a same-named path elsewhere in the tree (e.g.
// upstream also has `path="new"` under settings custom visualizations, which
// we want to keep visible).

export const BLOCKED_ADMIN_ROUTES: readonly string[] = [
  // Embedding upsells + subtree.
  "admin/embedding",

  // AI / Metabot admin surface — not part of the IAS product bundle.
  "admin/metabot",

  // Erroring Questions upsell + Logs viewer (customer admins shouldn't see
  // instance internals).
  "admin/tools/errors",
  "admin/tools/logs",

  // Manual user provisioning — IAS provisions via OIDC token exchange.
  "admin/people/new",
  "admin/people/:userId/reset",

  // Instance-owner-only concerns; the customer is not the operator.
  "admin/settings/updates",
  "admin/settings/authentication",
  "admin/settings/remote-sync",
  "admin/settings/email",
  "admin/settings/slack",
  "admin/settings/custom-visualizations",
  "admin/settings/license",
  "admin/settings/appearance",
  // Appearance uses the `whitelabel` route when the license is missing (the
  // nav renders a `whitelabel` upsell instead of `appearance`); block both.
  "admin/settings/whitelabel",
  "admin/settings/cloud",

  // Full-page upsells outside `admin/*` — the primitive null-stub blanks the
  // card content, and these entries 404 the containing page so the user gets
  // a proper error rather than an empty frame.
  "data-studio/transforms/library",
  "data-studio/dependencies",
  "data-studio/dependency-diagnostics",
  "data-studio/schema-viewer",
];

// Compile each blocklist entry into a regex that treats `:paramName` segments
// as a single-path-segment wildcard so `admin/people/:userId/reset` matches
// `/admin/people/42/reset`. The regex requires the entire input to be either
// the exact match or a descendant (trailing `/…`), which is what the previous
// string-based `startsWith` check enforced but for parameterized paths.
const BLOCKED_ROUTE_REGEXES = BLOCKED_ADMIN_ROUTES.map((pattern) => {
  const segments = pattern
    .split("/")
    .map((segment) =>
      segment.startsWith(":")
        ? "[^/]+"
        : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    )
    .join("/");
  return new RegExp(`^${segments}(?:/|$)`);
});

// True if `currentPath` matches a blocked entry exactly, OR is a descendant of
// one (so blocking `admin/embedding` also hides `admin/embedding/modular` etc.
// without listing each child). Leading slashes are tolerated so nav link paths
// like `/admin/embedding` compare equal to blocklist entries.
export function isBlockedAdminRoute(currentPath: string): boolean {
  const normalized = currentPath.replace(/^\/+/, "");
  return BLOCKED_ROUTE_REGEXES.some((re) => re.test(normalized));
}
